import React, { useMemo, useEffect, useCallback } from "react";
import { FieldError } from "react-hook-form";
import { TextField } from "@mui/material";
import Input from "components/Input";
import { EMAIL_PATTERN_REGEX } from "utils/formatByText";
import * as Styled from "./styles";
import { CredentialProperty } from "../../types";
import { getCredentialLabel } from "../Review/reviewConfig";
import { useProviderFormContext } from "../../Context/ValidationContext";
import {
  applySchemaConstValuesToForm,
  getConstCredentialFields,
  isConstSchemaProperty,
} from "../../utils/schemaConstFields";

const Credentials: React.FC = () => {
  const {
    selectedProvider,
    register,
    setValue,
    trigger,
    watch,
    getValues,
    formErrors: errors,
    internalReadOnly: readOnly,
  } = useProviderFormContext();

  useEffect(() => {
    applySchemaConstValuesToForm(selectedProvider?.schema, setValue, getValues);
  }, [selectedProvider?.schema, selectedProvider?.id, setValue, getValues]);

  const autoFilledCredentialFields = useMemo(
    () => getConstCredentialFields(selectedProvider?.schema),
    [selectedProvider?.schema],
  );

  const credentialFields = useMemo(() => {
    if (!selectedProvider?.schema?.properties?.credentials?.properties) {
      return [];
    }

    const credentialsProperties =
      selectedProvider.schema.properties.credentials.properties;
    return Object.keys(credentialsProperties)
      .filter(
        (key) =>
          key !== "type" && !isConstSchemaProperty(credentialsProperties[key]),
      )
      .map((key) => {
        const prop = credentialsProperties[key];
        const titleFromSchema =
          prop && typeof prop === "object" && "title" in prop
            ? (prop as { title?: string }).title
            : undefined;
        const descriptionFromSchema =
          prop && typeof prop === "object" && "description" in prop
            ? (prop as { description?: string }).description
            : undefined;
        return {
          name: key,
          label: getCredentialLabel(key, titleFromSchema),
          type: prop?.type ?? "text",
          required: (prop as CredentialProperty | undefined)?.required ?? false,
          placeholder:
            descriptionFromSchema && descriptionFromSchema.trim() !== ""
              ? descriptionFromSchema.trim()
              : "Digite aqui",
        };
      });
  }, [selectedProvider]);

  const isEmailField = (fieldName: string): boolean => {
    return fieldName.toLowerCase() === "email";
  };

  const setupEmailValidation = useCallback(() => {
    if (!watch || !trigger) {
      return undefined;
    }

    const emailField = credentialFields.find(
      (f) => f.name.toLowerCase() === "email",
    );
    if (!emailField) {
      return undefined;
    }

    const fieldName = `credentials.${emailField.name}` as const;
    const unsubscribe = watch((value, { name, type }) => {
      if (
        name === fieldName &&
        type === "change" &&
        value.credentials?.[emailField.name]
      ) {
        trigger(fieldName);
      }
    });

    return () => {
      if (unsubscribe && typeof unsubscribe.unsubscribe === "function") {
        unsubscribe.unsubscribe();
      }
    };
  }, [credentialFields, watch, trigger]);

  useEffect(() => {
    setupEmailValidation();
  }, [setupEmailValidation]);

  const getRegisterOptions = (field: { name: string; required: boolean }) => {
    const options: {
      required?: string | boolean;
      pattern?: {
        value: RegExp;
        message: string;
      };
      validate?: (value: string) => boolean | string;
    } = {
      required: "Campo obrigatório",
    };

    if (isEmailField(field.name)) {
      options.validate = (value: string) => {
        if (!value || value.trim() === "") {
          return "Campo obrigatório";
        }
        if (!EMAIL_PATTERN_REGEX.test(value.trim())) {
          return "Endereço de e-mail inválido";
        }
        return true;
      };
    }

    return options;
  };

  const renderCredentialsFields = () => {
    if (!selectedProvider) {
      return (
        <Styled.EmptyStateTypography variant="body2">
          Selecione um provedor para exibir as credenciais necessárias.
        </Styled.EmptyStateTypography>
      );
    }

    return (
      <>
        {autoFilledCredentialFields.map(({ key }) => (
          <input
            key={`const-credential-${key}`}
            type="hidden"
            {...register(`credentials.${key}` as `credentials.${string}`, {
              required: false,
            })}
          />
        ))}
        <input type="hidden" {...register("type", { required: false })} />
        <TextField
          disabled
          label="Provedor"
          fullWidth
          size="small"
          variant="outlined"
          value={selectedProvider?.name || "Selecione um provedor"}
          margin="none"
        />
        {credentialFields.map((field) => {
          const fieldName = `credentials.${field.name}` as const;
          const credentialsErrors = errors.credentials as
            | Record<string, FieldError>
            | undefined;
          const fieldError = credentialsErrors?.[field.name] as
            | FieldError
            | undefined;

          const registerOptions = getRegisterOptions(field);

          return (
            <Input
              required
              key={field.name}
              name={fieldName}
              label={`${field.label}`}
              error={fieldError}
              register={register}
              fullWidth
              placeholder={field.placeholder}
              disabled={readOnly}
              registerOptions={registerOptions}
            />
          );
        })}
      </>
    );
  };

  return (
    <Styled.Container>
      <Styled.ImportantNotice>
        <Styled.InfoIconStyled />
        <Styled.InfoTypography variant="body2">
          <Styled.ImportantLabel>Importante: </Styled.ImportantLabel>
          Caso não encontre os dados necessários para preenchimento das
          informações no portal do Provedor, entre em contato com o suporte
          desse Provedor.
        </Styled.InfoTypography>
      </Styled.ImportantNotice>

      <Styled.FormContainer>{renderCredentialsFields()}</Styled.FormContainer>
    </Styled.Container>
  );
};

export default Credentials;
